import { Plus, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { CommercialDeal } from "@/hooks/useLeadCommercialProfile";

function formatCurrency(v: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
}

interface LeadDealsSectionProps {
  deals: CommercialDeal[];
  onNewDeal: () => void;
  onDealClick: (deal: CommercialDeal) => void;
}

export function LeadDealsSection({ deals, onNewDeal, onDealClick }: LeadDealsSectionProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Handshake className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">Negociações ({deals.length})</h3>
        </div>
        <Button variant="outline" size="sm" onClick={onNewDeal} className="h-7 text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" />
          Nova
        </Button>
      </div>

      {deals.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">Nenhuma negociação vinculada.</p>
      ) : (
        <div className="space-y-2">
          {deals.map((deal) => {
            const statusLabel = deal.status === "won" ? "Ganho" : deal.status === "lost" ? "Perdido" : "Aberto";
            const statusColor = deal.status === "won"
              ? "bg-green-500/10 text-green-700 dark:text-green-400"
              : deal.status === "lost"
                ? "bg-red-500/10 text-red-700 dark:text-red-400"
                : "bg-blue-500/10 text-blue-700 dark:text-blue-400";

            return (
              <button
                key={deal.id}
                onClick={() => onDealClick(deal)}
                className="w-full text-left rounded-lg border bg-card p-3 hover:bg-accent/50 transition-colors space-y-1.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate">{deal.title}</span>
                  <span className="text-sm font-semibold shrink-0">{formatCurrency(deal.amount)}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" className={cn("text-[10px] px-1.5 py-0", statusColor)}>
                    {statusLabel}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    <div className={cn("w-1.5 h-1.5 rounded-full mr-1", deal.stage_color)} />
                    {deal.stage_name}
                  </Badge>
                  {deal.probability > 0 && (
                    <span className="text-[10px] text-muted-foreground">{deal.probability}%</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
